import { useState } from 'react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { 
  AlertCircle, 
  Calendar, 
  FileText, 
  User, 
  Clock,
  Users,
  CheckCircle,
  XCircle,
  Lock,
} from 'lucide-react';
import { useApproveVersion, useRejectVersion } from '@/hooks/useVersionApproval';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ViewDocumentLink } from './ViewDocumentLink';
import { formatDisplayName } from '@/lib/formatName';
import type { PolicyVersion } from '@/hooks/usePolicy';

interface VersionApprovalDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  version: PolicyVersion;
  policyId: string;
  policyName: string;
}

export function VersionApprovalDialog({ 
  open, 
  onOpenChange, 
  version, 
  policyId, 
  policyName 
}: VersionApprovalDialogProps) {
  const [showApprovalConfirmation, setShowApprovalConfirmation] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  const approveVersion = useApproveVersion();
  const rejectVersion = useRejectVersion();

  const isProcessing = approveVersion.isPending || rejectVersion.isPending;

  function handleApproveClick() {
    setShowApprovalConfirmation(true);
  }

  function handleApproveConfirm() {
    approveVersion.mutate({
      versionId: version.id,
      policyId,
      isSubstantive: version.is_substantive ?? false,
      calculatedNextReview: version.calculated_next_review ?? null,
      reviewCadenceMonths: version.review_cadence_months ?? null,
    }, {
      onSuccess: () => {
        setShowApprovalConfirmation(false);
        onOpenChange(false);
      },
    });
  }

  function handleReject() {
    if (!rejectionReason.trim()) return;

    rejectVersion.mutate({
      versionId: version.id,
      policyId,
      rejectionReason: rejectionReason.trim(),
    }, {
      onSuccess: () => {
        setShowRejectDialog(false);
        setRejectionReason('');
        onOpenChange(false);
      },
    });
  }

  function handleOpenRejectDialog() {
    setShowRejectDialog(true);
  }

  const uploaderName = formatDisplayName(version.created_by_profile?.full_name ?? 'Unknown', version.created_by_profile?.credentials);
  const uploadDate = format(new Date(version.created_at), 'MMMM d, yyyy \'at\' h:mm a');

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Review Version {version.version_number}
            </DialogTitle>
            <DialogDescription>
              Review and approve or reject this pending version for "{policyName}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Version Details */}
            <div className="grid gap-3 rounded-md border bg-muted/30 p-4">
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Uploaded by:</span>
                <span className="font-medium">{uploaderName}</span>
              </div>
              
              <div className="flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Upload date:</span>
                <span className="font-medium">{uploadDate}</span>
              </div>

              <div className="flex items-center gap-2 text-sm">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">Document:</span>
                <ViewDocumentLink 
                  fileUrl={version.file_url} 
                  policyId={policyId}
                  title={`${policyName} - v${version.version_number}`}
                />
              </div>
            </div>

            {/* Update Type Badge */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Update type:</span>
              {version.is_substantive ? (
                <Badge variant="default" className="gap-1">
                  <Users className="h-3 w-3" />
                  Substantive (requires re-acknowledgment)
                </Badge>
              ) : (
                <Badge variant="secondary">
                  Minor update
                </Badge>
              )}
            </div>

            {/* Review Date Info (for substantive updates) */}
            {version.is_substantive && version.calculated_next_review && (
              <div className="flex items-center gap-2 text-sm rounded-md border border-primary/20 bg-primary/5 p-3">
                <Calendar className="h-4 w-4 text-primary" />
                <span className="text-muted-foreground">New next review date:</span>
                <span className="font-medium text-primary">
                  {format(new Date(version.calculated_next_review), 'MMMM d, yyyy')}
                </span>
                <span className="text-muted-foreground">
                  ({version.review_cadence_months} month cadence)
                </span>
              </div>
            )}

            {/* Acknowledgment Notice */}
            {version.is_substantive && (
              <div className="flex items-start gap-2 text-sm text-status-pending bg-status-pending/10 rounded-md p-3">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  Approving this version will clear existing acknowledgments and require all assigned staff to acknowledge the new version.
                </span>
              </div>
            )}

            {/* Version Notes */}
            {version.notes && (
              <div className="space-y-1.5">
                <Label className="text-sm text-muted-foreground">Version notes:</Label>
                <div className="rounded-md border bg-background p-3 text-sm">
                  {version.notes}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleOpenRejectDialog}
              disabled={isProcessing}
              className="gap-1.5"
            >
              <XCircle className="h-4 w-4" />
              Reject
            </Button>
            <Button
              onClick={handleApproveClick}
              disabled={isProcessing}
              className="gap-1.5"
            >
              <CheckCircle className="h-4 w-4" />
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approval Confirmation Dialog */}
      <AlertDialog open={showApprovalConfirmation} onOpenChange={setShowApprovalConfirmation}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-amber-500" />
              Approve and Lock This Policy?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p className="text-muted-foreground">Once approved:</p>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2">
                    <Lock className="h-4 w-4 mt-0.5 shrink-0 text-teal-600" />
                    <span>This policy will be locked and cannot be edited</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FileText className="h-4 w-4 mt-0.5 shrink-0 text-teal-600" />
                    <span>A new version will be required for any changes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Users className="h-4 w-4 mt-0.5 shrink-0 text-teal-600" />
                    <span>All assigned staff will be notified to acknowledge</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Clock className="h-4 w-4 mt-0.5 shrink-0 text-teal-600" />
                    <span>The approval will be timestamped and immutable</span>
                  </li>
                </ul>
                <p className="text-sm font-medium text-foreground pt-2">
                  Are you sure you want to approve "{policyName}" version {version.version_number}?
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={approveVersion.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleApproveConfirm}
              disabled={approveVersion.isPending}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Lock className="h-4 w-4 mr-1.5" />
              {approveVersion.isPending ? 'Approving...' : 'Approve & Lock'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Rejection Reason Dialog */}
      <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Version {version.version_number}</AlertDialogTitle>
            <AlertDialogDescription>
              Please provide a reason for rejecting this version. The uploader will be notified.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-4">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setRejectionReason('');
              setShowRejectDialog(false);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              disabled={!rejectionReason.trim() || rejectVersion.isPending}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {rejectVersion.isPending ? 'Rejecting...' : 'Confirm Rejection'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
